import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  Stack,
  Divider,
  Switch,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import { CheckCircle, Download, Plus, Eye, EyeOff } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { ImageUploader } from '../components/common/ImageUploader';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { changePassword, logout } from '../services/authService';
import { getProfile, updateProfile } from '../services/profileService';
import { getNotificationPreferences, updateNotificationPreferences } from '../services/notificationService';
import { getAuthUser, setUserProfile } from '../utils/tokenManager';
import { 
  setCachedProfileImage, 
  setCachedBusinessImage, 
  removeCachedProfileImage, 
  removeCachedBusinessImage 
} from '../utils/imageCache';
import type { ProfileData } from '../types/profile';
import type { AlertPreferences } from '../types/notification';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

/* Hidden - Integrations tab is commented out
interface IntegrationItemProps {
  name: string;
  description: string;
  logo: string;
  enabled: boolean;
}

const IntegrationItem = ({ name, description, logo, enabled: initialEnabled }: IntegrationItemProps) => {
  const [enabled, setEnabled] = useState(initialEnabled);

  return (
    <Box
      sx={{
        py: 2.5,
        borderBottom: '1px solid #E5E7EB',
        '&:last-child': {
          borderBottom: 'none'
        }
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 2, sm: 3 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={2} alignItems="center" flex={1} minWidth={0}>
          <Avatar
            src={logo}
            alt={name}
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'white',
              border: '1px solid #E5E7EB'
            }}
          />
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: '#111827',
                mb: 0.5
              }}
            >
              {name}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
                fontWeight: 500
              }}
            >
              {description}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          sx={{
            pl: { xs: 7, sm: 0 },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Link
            href="#"
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#9CA3AF',
              textDecoration: 'none',
              '&:hover': {
                color: '#111827'
              }
            }}
          >
            Learn More
          </Link>
          <Switch
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            sx={{
              width: 44,
              height: 24,
              padding: 0,
              '& .MuiSwitch-switchBase': {
                padding: 0,
                margin: '4px',
                transitionDuration: '200ms',
                '&.Mui-checked': {
                  transform: 'translateX(20px)',
                  color: '#fff',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#4F46E5',
                    opacity: 1,
                    border: 0,
                  },
                },
                '&.Mui-focusVisible .MuiSwitch-thumb': {
                  color: '#4F46E5',
                  border: '6px solid #fff',
                },
              },
              '& .MuiSwitch-thumb': {
                width: 14,
                height: 14,
                boxShadow: 'none'
              },
              '& .MuiSwitch-track': {
                borderRadius: 12,
                backgroundColor: '#E5E7EB',
                opacity: 1,
                border: '1px solid #D1D5DB'
              },
            }}
          />
        </Stack>
      </Stack>
    </Box>
  );
};
*/

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  const [currentTab, setCurrentTab] = useState(0);

  // Profile data states
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileUpdateSuccess, setProfileUpdateSuccess] = useState<string | null>(null);
  const [profileUpdateError, setProfileUpdateError] = useState<string | null>(null);

  // Form field states
  const [formData, setFormData] = useState({
    first_name: '',
    second_name: '',
    business_name: '',
    business_email: '',
    website: '',
    about_business: '',
    state: '',
    country: '',
    city: '',
    zipcode: ''
  });

  // Handle navigation state to set the correct tab
  useEffect(() => {
    const state = location.state as { tab?: number } | null;
    if (state && typeof state.tab === 'number') {
      setCurrentTab(state.tab);
    }
  }, [location]);

  // Fetch notification preferences on mount
  useEffect(() => {
    const fetchNotificationPreferences = async () => {
      try {
        setIsLoadingNotifications(true);
        setNotificationError(null);
        const response = await getNotificationPreferences();

        if (response.success && response.data) {
          setNotificationStatus(response.data.notification_status);
          setNotificationSettings(response.data.alert_preferences);
          console.log('✅ Notification preferences loaded:', response.data);
        } else {
          console.log('No notification preferences found, using defaults');
        }
      } catch (error) {
        console.error('Error fetching notification preferences:', error);
        // Use default values on error
      } finally {
        setIsLoadingNotifications(false);
      }
    };

    fetchNotificationPreferences();
  }, []);

  // Fetch profile data on mount
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoadingProfile(true);
        setProfileError(null);
        const response = await getProfile();

        if (response.success && response.data?.profile) {
          const profile = response.data.profile;
          setProfileData(profile);
          
          // Update cached profile in localStorage so header can access the latest data
          setUserProfile(profile);

          // Populate form fields with fetched data
          setFormData({
            first_name: profile.first_name || '',
            second_name: profile.second_name || '',
            business_name: profile.business_name || '',
            business_email: profile.business_email || '',
            website: profile.website ? profile.website.replace(/^https?:\/\//, '') : '',
            about_business: profile.about_business || '',
            state: profile.state || '',
            country: profile.country || '',
            city: profile.city || '',
            zipcode: profile.zipcode || ''
          });

          // Parse profile image and business logo from API response
          // Check multiple possible locations for image URLs
          const profileImageUrl = 
            profile.profile_image_url || 
            (response.data as any).profile_image_url ||
            profile.profile_image;
            
          const businessLogoUrl = 
            profile.business_image_url ||  // API uses business_image_url
            profile.business_logo_url || 
            (response.data as any).business_image_url ||
            (response.data as any).business_logo_url ||
            profile.business_logo;

          if (profileImageUrl) {
            console.log('Setting profile photo:', profileImageUrl);
            setProfilePhoto(profileImageUrl);
            // Cache the profile image
            setCachedProfileImage(profileImageUrl);
          }
          
          if (businessLogoUrl) {
            console.log('Setting business logo:', businessLogoUrl);
            setBusinessLogo(businessLogoUrl);
            // Cache the business image
            setCachedBusinessImage(businessLogoUrl);
          }

          console.log('Profile data loaded:', profile);
          console.log('Full response data:', response.data);
          console.log('Profile image URL:', profileImageUrl);
          console.log('Business logo URL:', businessLogoUrl);
        } else {
          setProfileError('Failed to load profile data');
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfileError(error instanceof Error ? error.message : 'Failed to load profile');
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchProfileData();
  }, []);

  // Check authentication provider on mount
  useEffect(() => {
    const user = getAuthUser();
    if (user && user.auth_provider) {
      const oauthProviders = ['google', 'facebook', 'apple', 'microsoft'];
      const isOAuth = oauthProviders.includes(user.auth_provider.toLowerCase());

      setIsFederatedLogin(isOAuth);

      if (isOAuth) {
        // Capitalize the provider name for display
        const displayName = user.auth_provider.charAt(0).toUpperCase() + user.auth_provider.slice(1);
        setAuthProviderName(displayName);
        console.log(`User logged in via ${displayName} OAuth - password change disabled`);
      }
    }
  }, []);

  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);


  // Check if user is using OAuth (federated login)
  const [isFederatedLogin, setIsFederatedLogin] = useState(false);
  const [authProviderName, setAuthProviderName] = useState<string>('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Profile photo state
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);

  // Business logo state
  const [businessLogo, setBusinessLogo] = useState<string | null>(null);

  // Notification settings states
  const [notificationStatus, setNotificationStatus] = useState<'on' | 'off'>('on');
  const [notificationSettings, setNotificationSettings] = useState<AlertPreferences>({
    survey_completed: true,
    reward_claimed: true,
    survey_management: true,
    negative_review: true,
    new_platform_review: true
  });
  const [notificationSuccess, setNotificationSuccess] = useState<string | null>(null);
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profileData?.id) {
      setProfileUpdateError('Profile ID not found');
      return;
    }

    try {
      setIsUpdatingProfile(true);
      setProfileUpdateError(null);
      setProfileUpdateSuccess(null);

      // Prepare update data
      const updateData = {
        first_name: formData.first_name,
        second_name: formData.second_name,
        business_name: formData.business_name,
        phone_number: profileData.phone_number, // Keep existing phone (read-only)
        business_email: formData.business_email,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        zipcode: formData.zipcode,
        website: formData.website ? `https://${formData.website.replace(/^https?:\/\//, '')}` : '',
        about_business: formData.about_business
      };

      console.log('Updating profile with data:', updateData);
      const response = await updateProfile(profileData.id, updateData);

      if (response.success) {
        setProfileUpdateSuccess('Profile updated successfully!');

        // Update profile data with the response
        if (response.data?.profile) {
          setProfileData(response.data.profile);
        }

        // Success message will be cleared manually or on next action
      } else {
        setProfileUpdateError(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setProfileUpdateError(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleFormFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };



  const handleNotificationToggle = (key: keyof AlertPreferences) => {
    setNotificationSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveNotifications = async () => {
    try {
      setIsSavingNotifications(true);
      setNotificationError(null);
      setNotificationSuccess(null);

      const response = await updateNotificationPreferences({
        notification_status: notificationStatus,
        alert_preferences: notificationSettings
      });

      if (response.success) {
        setNotificationSuccess('Notification preferences saved successfully!');
        console.log('✅ Notification preferences updated:', response.data);
      } else {
        setNotificationError(response.message || 'Failed to update notification preferences');
      }
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      setNotificationError(error instanceof Error ? error.message : 'Failed to update notification preferences');
    } finally {
      setIsSavingNotifications(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      setIsChangingPassword(true);
      await changePassword(currentPassword, newPassword);

      setPasswordSuccess('Password changed successfully! You will be logged out in 3 seconds...');

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Logout immediately
      await logout();
      navigate('/signin');
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F9FAFB' }}>
      <Sidebar user={userData} />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />

        <Box sx={{ p: { xs: 3, md: 4, lg: 6 } }}>
          {/* Page Heading */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: '1.5rem',
              color: '#111827',
              mb: 4
            }}
          >
            Settings
          </Typography>

          {/* Tabs */}
          <Box sx={{
            borderBottom: 1,
            borderColor: '#E5E7EB',
            mb: 3
          }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#6B7280',
                  minHeight: 48,
                  px: 0,
                  mr: 5,
                  '&.Mui-selected': {
                    color: '#4F46E5',
                    fontWeight: 600
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#4F46E5',
                  height: 2
                }
              }}
            >
              <Tab label="Profile" />
              <Tab label="Password" />
              <Tab label="Team" />
              <Tab label="Notification" />
              {/* <Tab label="Integrations" /> */}
              <Tab label="Billing" />
            </Tabs>
          </Box>

          {/* Profile Tab */}
          <TabPanel value={currentTab} index={0}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: '#111827',
                  mb: 0.5
                }}
              >
                Profile
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.875rem'
                }}
              >
                Update your personal information and profile settings
              </Typography>
            </Box>

            {/* Loading State */}
            {isLoadingProfile && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#4F46E5' }} />
              </Box>
            )}

            {/* Error State */}
            {profileError && !isLoadingProfile && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {profileError}
              </Alert>
            )}

            {/* Profile Form */}
            {!isLoadingProfile && !profileError && (
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ maxWidth: 800 }}
              >
                {/* Success Alert */}
                {profileUpdateSuccess && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {profileUpdateSuccess}
                  </Alert>
                )}

                {/* Error Alert */}
                {profileUpdateError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {profileUpdateError}
                  </Alert>
                )}

                <Stack spacing={4}>
                  {/* Profile Photo */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      Profile Photo
                    </Typography>
                    <ImageUploader
                      currentImageUrl={profilePhoto || userData.avatar}
                      onUploadSuccess={(imageUrl) => {
                        setProfilePhoto(imageUrl);
                        // Update cache with new image
                        setCachedProfileImage(imageUrl);
                        setProfileUpdateSuccess('Profile photo updated successfully!');
                      }}
                      onUploadError={(error) => {
                        setProfileUpdateError(error);
                      }}
                      buttonText="Update"
                      imageSize={48}
                      imageShape="square"
                      folderType="profile-images"
                      imageType="profile"
                      showRemoveButton={true}
                      onRemove={() => {
                        setProfilePhoto(null);
                        // Clear cached profile image
                        removeCachedProfileImage();
                        // TODO: Call API to remove profile photo from server
                        console.log('Profile photo removed');
                      }}
                      showHelperText={false}
                    />
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Business Logo */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      Business Logo
                    </Typography>
                    <ImageUploader
                      currentImageUrl={businessLogo || undefined}
                      onUploadSuccess={(imageUrl) => {
                        console.log('Business logo upload success, setting URL:', imageUrl);
                        setBusinessLogo(imageUrl);
                        // Update cache with new image
                        setCachedBusinessImage(imageUrl);
                        setProfileUpdateSuccess('Business logo updated successfully!');
                      }}
                      onUploadError={(error) => {
                        setProfileUpdateError(error);
                      }}
                      buttonText="Upload"
                      imageSize={64}
                      imageShape="square"
                      folderType="business-images"
                      imageType="business"
                      showRemoveButton={true}
                      onRemove={() => {
                        setBusinessLogo(null);
                        // Clear cached business image
                        removeCachedBusinessImage();
                        // TODO: Call API to remove business logo from server
                        console.log('Business logo removed');
                      }}
                      showHelperText={true}
                      helperText="Recommended size: 512x512px. Max file size: 5MB. Supports PNG and JPG."
                    />
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* First & Last Name */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      First & Last Name
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        value={formData.first_name}
                        onChange={(e) => handleFormFieldChange('first_name', e.target.value)}
                        placeholder="First Name"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#D1D5DB'
                            },
                            '&:hover fieldset': {
                              borderColor: '#9CA3AF'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4F46E5'
                            }
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        value={formData.second_name}
                        onChange={(e) => handleFormFieldChange('second_name', e.target.value)}
                        placeholder="Last Name"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#D1D5DB'
                            },
                            '&:hover fieldset': {
                              borderColor: '#9CA3AF'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4F46E5'
                            }
                          }
                        }}
                      />
                    </Stack>
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Phone Number */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      Phone Number
                    </Typography>
                    <TextField
                      fullWidth
                      value={profileData?.phone_number || ''}
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: '#F9FAFB',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          }
                        }
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Email Address */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      Email Address
                    </Typography>
                    <TextField
                      fullWidth
                      type="email"
                      value={formData.business_email}
                      placeholder="email@example.com"
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: '#F9FAFB',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          }
                        }
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Business Name */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      Business Name
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.business_name}
                      onChange={(e) => handleFormFieldChange('business_name', e.target.value)}
                      placeholder="Enter your business name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5'
                          }
                        }
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Business Description */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      About Your Business
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.about_business}
                      onChange={(e) => handleFormFieldChange('about_business', e.target.value)}
                      placeholder="Write about your business"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5'
                          }
                        }
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Website */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      Website
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 2,
                          bgcolor: '#F9FAFB',
                          border: '1px solid #D1D5DB',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                          color: '#111827',
                          fontSize: '0.875rem'
                        }}
                      >
                        https://
                      </Box>
                      <TextField
                        fullWidth
                        value={formData.website}
                        onChange={(e) => handleFormFieldChange('website', e.target.value)}
                        placeholder="www.yourwebsite.com"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0 8px 8px 0',
                            '& fieldset': {
                              borderColor: '#D1D5DB'
                            },
                            '&:hover fieldset': {
                              borderColor: '#9CA3AF'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4F46E5'
                            }
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* State */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      State / Province
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.state}
                      onChange={(e) => handleFormFieldChange('state', e.target.value)}
                      placeholder="Enter state or province"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5'
                          }
                        }
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Country */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      Country
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={formData.country}
                        onChange={(e) => handleFormFieldChange('country', e.target.value)}
                        sx={{
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4F46E5'
                          }
                        }}
                      >
                        <MenuItem value="">Select a country</MenuItem>
                        <MenuItem value="us">United States</MenuItem>
                        <MenuItem value="uk">United Kingdom</MenuItem>
                        <MenuItem value="ca">Canada</MenuItem>
                        <MenuItem value="au">Australia</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* City */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      City
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.city}
                      onChange={(e) => handleFormFieldChange('city', e.target.value)}
                      placeholder="Enter city"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5'
                          }
                        }
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Zip / Postal Code */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1
                      }}
                    >
                      Zip / Postal Code
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.zipcode}
                      onChange={(e) => handleFormFieldChange('zipcode', e.target.value)}
                      placeholder="Enter zip or postal code"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5'
                          }
                        }
                      }}
                    />
                  </Box>
                </Stack>

                {/* Submit Button */}
                <Box sx={{ mt: 6 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isUpdatingProfile}
                    sx={{
                      bgcolor: '#4F46E5',
                      color: 'white',
                      textTransform: 'none',
                      px: 4,
                      py: 1.5,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#4338CA',
                        boxShadow: 'none'
                      },
                      '&:disabled': {
                        bgcolor: '#9CA3AF',
                        color: 'white'
                      }
                    }}
                  >
                    {isUpdatingProfile ? (
                      <>
                        <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
                        Updating...
                      </>
                    ) : (
                      'Update Profile'
                    )}
                  </Button>
                </Box>
              </Box>
            )}
          </TabPanel>

          {/* Password Tab */}
          <TabPanel value={currentTab} index={1}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: '#111827',
                  mb: 0.5
                }}
              >
                Change Password
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.875rem'
                }}
              >
                {isFederatedLogin
                  ? `You're signed in with ${authProviderName}`
                  : 'Update your password to keep your account secure'}
              </Typography>
            </Box>

            {isFederatedLogin ? (
              // Show info message for federated login users
              <Alert
                severity="info"
                sx={{
                  maxWidth: 800,
                  bgcolor: '#EEF2FF',
                  border: '1px solid #C7D2FE',
                  '& .MuiAlert-icon': {
                    color: '#4F46E5'
                  }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#111827', mb: 1 }}>
                  Password Change Not Available
                </Typography>
                <Typography sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
                  You're using {authProviderName} to sign in to your account. Password management is handled
                  through your {authProviderName} account settings. If you need to update your password,
                  please do so directly in your {authProviderName} account.
                </Typography>
              </Alert>
            ) : (
              // Show password change form for email/phone users
              <Box
                component="form"
                onSubmit={handlePasswordChange}
                sx={{ maxWidth: 800 }}
              >
                <Stack spacing={4}>
                  {/* Success Message */}
                  {passwordSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {passwordSuccess}
                    </Alert>
                  )}

                  {/* Error Message */}
                  {passwordError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {passwordError}
                    </Alert>
                  )}

                  {/* Current Password */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1.5
                      }}
                    >
                      Current Password
                    </Typography>
                    <TextField
                      fullWidth
                      type={showCurrentPassword ? 'text' : 'password'}
                      placeholder="Enter your current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={isChangingPassword}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5'
                          }
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              edge="end"
                              sx={{ color: '#6B7280' }}
                            >
                              {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* New Password */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1.5
                      }}
                    >
                      New Password
                    </Typography>
                    <Box>
                      <TextField
                        fullWidth
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="Enter your new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={isChangingPassword}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#D1D5DB'
                            },
                            '&:hover fieldset': {
                              borderColor: '#9CA3AF'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4F46E5'
                            }
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                edge="end"
                                sx={{ color: '#6B7280' }}
                              >
                                {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6B7280',
                          fontSize: '0.75rem',
                          display: 'block',
                          mt: 1
                        }}
                      >
                        Password must be at least 8 characters long
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  {/* Confirm New Password */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                    gap: 3,
                    alignItems: 'start'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#111827',
                        pt: 1.5
                      }}
                    >
                      Confirm Password
                    </Typography>
                    <TextField
                      fullWidth
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isChangingPassword}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: '#D1D5DB'
                          },
                          '&:hover fieldset': {
                            borderColor: '#9CA3AF'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4F46E5'
                          }
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              sx={{ color: '#6B7280' }}
                            >
                              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                </Stack>

                {/* Submit Button */}
                <Box sx={{ mt: 6 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isChangingPassword}
                    sx={{
                      bgcolor: '#4F46E5',
                      color: 'white',
                      textTransform: 'none',
                      px: 4,
                      py: 1.5,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#4338CA',
                        boxShadow: 'none'
                      },
                      '&:disabled': {
                        bgcolor: '#D1D5DB',
                        color: '#9CA3AF'
                      }
                    }}
                  >
                    {isChangingPassword ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} color="inherit" />
                        <span>Updating...</span>
                      </Stack>
                    ) : (
                      'Update Password'
                    )}
                  </Button>
                </Box>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={currentTab} index={2}>
            <Typography variant="body1" sx={{ color: '#6B7280' }}>
              Team settings content coming soon...
            </Typography>
          </TabPanel>

          {/* Notification Tab */}
          <TabPanel value={currentTab} index={3}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: '#111827',
                  mb: 0.5
                }}
              >
                Notification Preferences
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.875rem'
                }}
              >
                Choose which notifications you want to receive
              </Typography>
            </Box>

            {notificationSuccess && (
              <Alert severity="success" sx={{ mb: 4 }}>
                {notificationSuccess}
              </Alert>
            )}

            <Box sx={{ maxWidth: 800 }}>
              <Stack spacing={0}>
                {/* Survey Completed */}
                <Box
                  sx={{
                    py: 3,
                    borderBottom: '1px solid #E5E7EB'
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ flex: 1, pr: 3 }}>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#111827',
                          mb: 0.5
                        }}
                      >
                        Survey Completed
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: '#6B7280',
                          fontWeight: 500
                        }}
                      >
                        Get notified when a customer completes your survey
                      </Typography>
                    </Box>
                    <Switch
                      checked={notificationSettings.survey_completed}
                      onChange={() => handleNotificationToggle('survey_completed')}
                      disabled={isLoadingNotifications}
                      sx={{
                        width: 44,
                        height: 24,
                        padding: 0,
                        '& .MuiSwitch-switchBase': {
                          padding: 0,
                          margin: '4px',
                          transitionDuration: '200ms',
                          '&.Mui-checked': {
                            transform: 'translateX(20px)',
                            color: '#fff',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#4F46E5',
                              opacity: 1,
                              border: 0,
                            },
                          },
                        },
                        '& .MuiSwitch-thumb': {
                          width: 14,
                          height: 14,
                          boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 12,
                          backgroundColor: '#E5E7EB',
                          opacity: 1,
                          border: '1px solid #D1D5DB'
                        },
                      }}
                    />
                  </Stack>
                </Box>

                {/* Reward Claimed */}
                <Box
                  sx={{
                    py: 3,
                    borderBottom: '1px solid #E5E7EB'
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ flex: 1, pr: 3 }}>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#111827',
                          mb: 0.5
                        }}
                      >
                        Reward Claimed
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: '#6B7280',
                          fontWeight: 500
                        }}
                      >
                        Receive alerts when customers claim their rewards
                      </Typography>
                    </Box>
                    <Switch
                      checked={notificationSettings.reward_claimed}
                      onChange={() => handleNotificationToggle('reward_claimed')}
                      disabled={isLoadingNotifications}
                      sx={{
                        width: 44,
                        height: 24,
                        padding: 0,
                        '& .MuiSwitch-switchBase': {
                          padding: 0,
                          margin: '4px',
                          transitionDuration: '200ms',
                          '&.Mui-checked': {
                            transform: 'translateX(20px)',
                            color: '#fff',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#4F46E5',
                              opacity: 1,
                              border: 0,
                            },
                          },
                        },
                        '& .MuiSwitch-thumb': {
                          width: 14,
                          height: 14,
                          boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 12,
                          backgroundColor: '#E5E7EB',
                          opacity: 1,
                          border: '1px solid #D1D5DB'
                        },
                      }}
                    />
                  </Stack>
                </Box>

                {/* Survey Management */}
                <Box
                  sx={{
                    py: 3,
                    borderBottom: '1px solid #E5E7EB'
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ flex: 1, pr: 3 }}>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#111827',
                          mb: 0.5
                        }}
                      >
                        Survey Management
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: '#6B7280',
                          fontWeight: 500
                        }}
                      >
                        Notify when surveys are created, deleted, or disabled
                      </Typography>
                    </Box>
                    <Switch
                      checked={notificationSettings.survey_management}
                      onChange={() => handleNotificationToggle('survey_management')}
                      disabled={isLoadingNotifications}
                      sx={{
                        width: 44,
                        height: 24,
                        padding: 0,
                        '& .MuiSwitch-switchBase': {
                          padding: 0,
                          margin: '4px',
                          transitionDuration: '200ms',
                          '&.Mui-checked': {
                            transform: 'translateX(20px)',
                            color: '#fff',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#4F46E5',
                              opacity: 1,
                              border: 0,
                            },
                          },
                        },
                        '& .MuiSwitch-thumb': {
                          width: 14,
                          height: 14,
                          boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 12,
                          backgroundColor: '#E5E7EB',
                          opacity: 1,
                          border: '1px solid #D1D5DB'
                        },
                      }}
                    />
                  </Stack>
                </Box>

                {/* Negative Review */}
                <Box
                  sx={{
                    py: 3,
                    borderBottom: '1px solid #E5E7EB'
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ flex: 1, pr: 3 }}>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#111827',
                          mb: 0.5
                        }}
                      >
                        Negative Review Alert
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: '#6B7280',
                          fontWeight: 500
                        }}
                      >
                        Get immediate alerts for negative or low-rated reviews
                      </Typography>
                    </Box>
                    <Switch
                      checked={notificationSettings.negative_review}
                      onChange={() => handleNotificationToggle('negative_review')}
                      disabled={isLoadingNotifications}
                      sx={{
                        width: 44,
                        height: 24,
                        padding: 0,
                        '& .MuiSwitch-switchBase': {
                          padding: 0,
                          margin: '4px',
                          transitionDuration: '200ms',
                          '&.Mui-checked': {
                            transform: 'translateX(20px)',
                            color: '#fff',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#4F46E5',
                              opacity: 1,
                              border: 0,
                            },
                          },
                        },
                        '& .MuiSwitch-thumb': {
                          width: 14,
                          height: 14,
                          boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 12,
                          backgroundColor: '#E5E7EB',
                          opacity: 1,
                          border: '1px solid #D1D5DB'
                        },
                      }}
                    />
                  </Stack>
                </Box>

                {/* New Platform Review */}
                <Box
                  sx={{
                    py: 3,
                    borderBottom: '1px solid #E5E7EB'
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ flex: 1, pr: 3 }}>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#111827',
                          mb: 0.5
                        }}
                      >
                        New Platform Review
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: '#6B7280',
                          fontWeight: 500
                        }}
                      >
                        Notify when new reviews appear on connected platforms (Google, Facebook, etc.)
                      </Typography>
                    </Box>
                    <Switch
                      checked={notificationSettings.new_platform_review}
                      onChange={() => handleNotificationToggle('new_platform_review')}
                      disabled={isLoadingNotifications}
                      sx={{
                        width: 44,
                        height: 24,
                        padding: 0,
                        '& .MuiSwitch-switchBase': {
                          padding: 0,
                          margin: '4px',
                          transitionDuration: '200ms',
                          '&.Mui-checked': {
                            transform: 'translateX(20px)',
                            color: '#fff',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#4F46E5',
                              opacity: 1,
                              border: 0,
                            },
                          },
                        },
                        '& .MuiSwitch-thumb': {
                          width: 14,
                          height: 14,
                          boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 12,
                          backgroundColor: '#E5E7EB',
                          opacity: 1,
                          border: '1px solid #D1D5DB'
                        },
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>

              {/* Success/Error Messages */}
              {notificationSuccess && (
                <Alert severity="success" sx={{ mt: 3 }} onClose={() => setNotificationSuccess(null)}>
                  {notificationSuccess}
                </Alert>
              )}
              {notificationError && (
                <Alert severity="error" sx={{ mt: 3 }} onClose={() => setNotificationError(null)}>
                  {notificationError}
                </Alert>
              )}

              {/* Save Button */}
              <Box sx={{ mt: 6 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveNotifications}
                  disabled={isSavingNotifications || isLoadingNotifications}
                  sx={{
                    bgcolor: '#4F46E5',
                    color: 'white',
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#4338CA',
                      boxShadow: 'none'
                    },
                    '&:disabled': {
                      bgcolor: '#9CA3AF',
                      color: 'white'
                    }
                  }}
                >
                  {isSavingNotifications ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                      Saving...
                    </>
                  ) : (
                    'Save Preferences'
                  )}
                </Button>
              </Box>
            </Box>
          </TabPanel>

          {/* Integrations Tab - Hidden
          <TabPanel value={currentTab} index={4}>
            <Box
              sx={{
                border: '1px solid #C7D2FE',
                borderRadius: '8px',
                bgcolor: '#EEF2FF',
                p: { xs: 2.5, sm: 3 },
                mb: 4
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Avatar
                  src="https://landingfoliocom.imgix.net/store/collection/clarity-dashboard/images/previews/settings/3/avatar-female.png"
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '8px'
                  }}
                />
                <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: 320 } }}>
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#111827',
                      mb: 0.5
                    }}
                  >
                    Learn how to connect review platforms
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#6B7280',
                      fontWeight: 500
                    }}
                  >
                    Connect your social media and review platforms to manage all reviews in one place
                  </Typography>
                </Box>
                <Stack
                  direction={{ xs: 'row', md: 'row-reverse' }}
                  spacing={3}
                  sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: '#4F46E5',
                      color: 'white',
                      textTransform: 'none',
                      px: 4,
                      py: 1.5,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#4338CA',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    View Tutorial
                  </Button>
                  <Button
                    variant="text"
                    sx={{
                      textTransform: 'none',
                      color: '#6B7280',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      '&:hover': {
                        color: '#111827',
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    Dismiss
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
              sx={{ mb: 4 }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: '#111827',
                    mb: 0.5
                  }}
                >
                  Connect Review Platforms
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    fontWeight: 500
                  }}
                >
                  Manage reviews from all your platforms in one dashboard
                </Typography>
              </Box>
              <TextField
                placeholder="Search Platform"
                size="small"
                sx={{
                  width: { xs: '100%', sm: 280 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '& fieldset': {
                      borderColor: '#D1D5DB'
                    },
                    '&:hover fieldset': {
                      borderColor: '#9CA3AF'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4F46E5'
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: '#9CA3AF' }}>
                      <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </Box>
                  )
                }}
              />
            </Stack>

            <Stack spacing={3}>
              {[
                {
                  name: 'Google My Business',
                  description: 'Sync and manage Google reviews automatically',
                  logo: 'https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg',
                  enabled: true
                },
                {
                  name: 'Facebook Reviews',
                  description: 'Connect your Facebook business page reviews',
                  logo: 'https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg',
                  enabled: true
                },
                {
                  name: 'Instagram',
                  description: 'Monitor comments and engagement on posts',
                  logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png',
                  enabled: false
                },
                {
                  name: 'Yelp',
                  description: 'Import and respond to Yelp reviews',
                  logo: 'https://upload.wikimedia.org/wikipedia/commons/a/ad/Yelp_Logo.svg',
                  enabled: true
                },
                {
                  name: 'TripAdvisor',
                  description: 'Manage hotel and restaurant reviews',
                  logo: 'https://upload.wikimedia.org/wikipedia/commons/d/dd/TripAdvisor_Logo.svg',
                  enabled: false
                },
                {
                  name: 'Trustpilot',
                  description: 'Collect and showcase customer reviews',
                  logo: 'https://cdn.trustpilot.net/brand-assets/4.1.0/logo-black.svg',
                  enabled: false
                }
              ].map((platform, index) => (
                <IntegrationItem
                  key={index}
                  name={platform.name}
                  description={platform.description}
                  logo={platform.logo}
                  enabled={platform.enabled}
                />
              ))}
            </Stack>
          </TabPanel>
          */}

          <TabPanel value={currentTab} index={4}>
            {/* Subscription Header */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: '#111827'
                  }}
                >
                  Subscription Plan: <Box component="span" sx={{ color: '#4F46E5' }}>Standard</Box>
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    fontWeight: 500,
                    mt: 0.5
                  }}
                >
                  Monthly Plan
                </Typography>
              </Box>
              <Button
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  color: '#6B7280',
                  borderColor: '#D1D5DB',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: '8px',
                  '&:hover': {
                    borderColor: '#9CA3AF',
                    bgcolor: '#F9FAFB',
                    color: '#111827'
                  }
                }}
              >
                Cancel Subscription
              </Button>
            </Stack>

            <Divider sx={{ borderColor: '#E5E7EB', mb: 2 }} />

            {/* Next Payment Info */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={{ xs: 1, sm: 2 }}
              sx={{ mb: 4 }}
            >
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                Your next payment is <Box component="span" sx={{ fontWeight: 700 }}>$59.00 USD</Box>, to be charged on{' '}
                <Box component="span" sx={{ fontWeight: 700 }}>April 08, 2022</Box>
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 500 }}>
                Your payment will be automatically renewed each month
              </Typography>
            </Stack>

            {/* Payment Method Section */}
            <Box
              sx={{
                bgcolor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                p: { xs: 2.5, sm: 4 },
                mb: 4
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '2fr 3fr' },
                  gap: { xs: 4, lg: 12 }
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#111827',
                      mb: 1
                    }}
                  >
                    Payment Method
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#6B7280',
                      fontWeight: 500
                    }}
                  >
                    Choose your preferred payment method for making future payments
                  </Typography>
                </Box>

                <Box>
                  <Stack spacing={2.5}>
                    {/* Primary Card */}
                    <Box
                      sx={{
                        position: 'relative',
                        border: '1px solid #4F46E5',
                        borderRadius: '12px',
                        bgcolor: '#EEF2FF',
                        p: 2.5,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16
                        }}
                      >
                        <CheckCircle size={24} color="#4F46E5" />
                      </Box>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box
                          component="img"
                          src="https://landingfoliocom.imgix.net/store/collection/clarity-dashboard/images/select-box/3/visa-logo.png"
                          alt="Visa"
                          sx={{ height: 24, width: 'auto', borderRadius: '4px' }}
                        />
                        <Box flex={1}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                            Visa ending 4331
                          </Typography>
                          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 500 }}>
                            Expiry 09/2024
                          </Typography>
                          <Stack direction="row" spacing={3} mt={2}>
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#9CA3AF',
                                cursor: 'not-allowed'
                              }}
                            >
                              Primary Card
                            </Typography>
                            <Link
                              href="#"
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#4F46E5',
                                textDecoration: 'none',
                                '&:hover': {
                                  color: '#4338CA'
                                }
                              }}
                            >
                              Edit
                            </Link>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Secondary Card */}
                    <Box
                      sx={{
                        position: 'relative',
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        bgcolor: 'white',
                        p: 2.5,
                        overflow: 'hidden'
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box
                          component="img"
                          src="https://landingfoliocom.imgix.net/store/collection/clarity-dashboard/images/select-box/3/visa-logo.png"
                          alt="Visa"
                          sx={{ height: 24, width: 'auto', borderRadius: '4px' }}
                        />
                        <Box flex={1}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                            Visa ending 5442
                          </Typography>
                          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 500 }}>
                            Expiry 08/2023
                          </Typography>
                          <Stack direction="row" spacing={3} mt={2}>
                            <Link
                              href="#"
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#6B7280',
                                textDecoration: 'none',
                                '&:hover': {
                                  color: '#111827'
                                }
                              }}
                            >
                              Set as Primary
                            </Link>
                            <Link
                              href="#"
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#4F46E5',
                                textDecoration: 'none',
                                '&:hover': {
                                  color: '#4338CA'
                                }
                              }}
                            >
                              Edit
                            </Link>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>

                  {/* Add New Payment Method */}
                  <Button
                    startIcon={<Plus size={16} />}
                    sx={{
                      textTransform: 'none',
                      color: '#6B7280',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      mt: 3,
                      '&:hover': {
                        color: '#111827',
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    Add New Payment Method
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Latest Transactions */}
            <Box sx={{ mb: 3 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
              >
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: '#111827'
                  }}
                >
                  Latest Transactions
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                      Sort by:
                    </Typography>
                    <Select
                      defaultValue="recent"
                      size="small"
                      variant="standard"
                      disableUnderline
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        '& .MuiSelect-select': {
                          paddingRight: '24px !important',
                          paddingLeft: 0
                        }
                      }}
                    >
                      <MenuItem value="recent">Recent</MenuItem>
                      <MenuItem value="oldest">Oldest</MenuItem>
                    </Select>
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download size={16} />}
                    sx={{
                      textTransform: 'none',
                      color: '#6B7280',
                      borderColor: '#D1D5DB',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      '&:hover': {
                        borderColor: '#9CA3AF',
                        bgcolor: '#F9FAFB'
                      }
                    }}
                  >
                    Export to CSV
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {/* Transactions Table */}
            <TableContainer
              sx={{
                border: '1px solid #D1D5DB',
                borderRadius: '12px',
                overflow: 'hidden'
              }}
            >
              <Table>
                <TableHead sx={{ bgcolor: '#F9FAFB', display: { xs: 'none', md: 'table-header-group' } }}>
                  <TableRow>
                    <TableCell sx={{ color: '#9CA3AF', fontSize: '0.875rem', fontWeight: 500, borderBottom: '1px solid #E5E7EB' }}>
                      Invoice
                    </TableCell>
                    <TableCell sx={{ color: '#9CA3AF', fontSize: '0.875rem', fontWeight: 500, borderBottom: '1px solid #E5E7EB' }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ color: '#9CA3AF', fontSize: '0.875rem', fontWeight: 500, borderBottom: '1px solid #E5E7EB' }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ color: '#9CA3AF', fontSize: '0.875rem', fontWeight: 500, borderBottom: '1px solid #E5E7EB' }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { invoice: 'Standard Plan - Feb 2022', date: '07 February, 2022', amount: '$59.00', status: 'Complete', statusColor: '#10B981' },
                    { invoice: 'Standard Plan - Jan 2022', date: '09 January, 2022', amount: '$59.00', status: 'Canceled', statusColor: '#EF4444' },
                    { invoice: 'Basic Plan - Dec 2021', date: '15 December, 2021', amount: '$29.00', status: 'Complete', statusColor: '#10B981' },
                    { invoice: 'Basic Plan - Nov 2021', date: '14 November, 2021', amount: '$29.00', status: 'Pending', statusColor: '#F59E0B' },
                    { invoice: 'Basic Plan - Oct 2021', date: '15 October, 2021', amount: '$29.00', status: 'Complete', statusColor: '#10B981' }
                  ].map((row, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ borderBottom: index === 4 ? 'none' : '1px solid #E5E7EB', py: 2 }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#111827' }}>
                          {row.invoice}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#6B7280',
                            fontWeight: 500,
                            mt: 0.5,
                            display: { xs: 'block', md: 'none' }
                          }}
                        >
                          {row.date}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: index === 4 ? 'none' : '1px solid #E5E7EB', display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                          {row.date}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: index === 4 ? 'none' : '1px solid #E5E7EB', textAlign: { xs: 'right', md: 'left' } }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#111827' }}>
                          {row.amount}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          justifyContent="flex-end"
                          sx={{ mt: 0.5, display: { xs: 'flex', md: 'none' } }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: row.statusColor
                            }}
                          />
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                            {row.status}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ borderBottom: index === 4 ? 'none' : '1px solid #E5E7EB', display: { xs: 'none', md: 'table-cell' } }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: row.statusColor
                            }}
                          />
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                            {row.status}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;

